/**
 * JSON Flow Generator Utility
 * Generates chatbot flow JSON based on categories and options
 */

import { ICategory } from "../types/categorySetup";
import { IConfigOption } from "../types/configOption";
import { IProduct } from "../types/productSetup";
import {
  ALL_OPTION_TYPES,
} from "../constants/optionTypes";
import { CATEGORY_TYPE_OPTIONS } from "../constants";

import {
  generateFirstTwoQuestions,
  generateNormalQuestion,
  generatePerWindowQuestions,
  parseDisplayCondition,
} from "./questions";

export interface IFlowQuestion {
  questionId: string;
  step: number;
  category: string;
  categoryName: string;
  optionType: number;
  optionTypeName: string;
  question: string;
  helperText?: string;
  questionType: number; // Numeric input type value (1-9) from ALL_INPUT_TYPES
  required: boolean;
  responsePath: string;
  displayCondition?: any; // Question-level display condition
  options: IFlowOption[];
  isCustomModified?: boolean; // true when user has manually edited the question text
}

export interface IFlowOption {
  optionId: string;
  optionText: string;
  description?: string;
  priceModifier?: number;
  basePrice?: number;
  price?: number;
  priceType?: "fixed" | "percentage";
  responseValue: string;
  value?: string; // dogma_value from configuration option
  inputType?: number; // Numeric input type value (1-9) from ALL_INPUT_TYPES
  optionType?: number; // Option type from the question
  optionTypeName?: string; // Option type name from the question
  displayCondition?: any;
  thumbnail?: string;
  withHornsUrl?: string;
  withoutHornsUrl?: string;
  validProducts?: string[]; // Product IDs this option is valid for
  numberOfFrames?: number; // Number of frames for this product/option
  numberOfSashes?: number; // Number of sashes per frame (from dogma_numberofsash)
  producttypecode?: number; // 1=Standard, 2=Arch, 3=Bays, 4=Coupled
  windowJson?: any; // Parsed dogma_proudctwindowjson — perspective corner data per bay side
  order?: number; // Order of the option within the question
  priceList?: any[]; // Parsed dogma_pricelist — conditional price rules
  optionUnit?: number; // 1=Per Window, 2=Per Sash, 3=Per Meter, 4=Per Unit
}

export interface IJsonFlow {
  productLine: string;
  chatbotConfig: {
    greeting: string;
    language: string;
    currency: string;
    units: string;
    defaultRegion: string;
  };
  questionFlow: IFlowQuestion[];
  metadata: {
    generatedAt: string;
    totalCategories: number;
    totalQuestions: number;
  };
}

// Glazing option types that must be asked per-frame (same as sash per-frame behaviour).
// Config Mode (49) is intentionally excluded — it is asked once per category to let the
// user choose Individual vs Same for All, then the options below are asked per-frame.
const GLAZING_PER_FRAME_OPTION_TYPES = new Set([81, 28, 42, 31]);

// Add Ons (category type 10) question order:
//   83 (Size Inclusion) → 82 (Add Ons multi-select) →
//   Head group (85 face → 50 size → 54 qty) →
//   Cill group (86 face → 53 size → 57 qty) →
//   Left Jamb group (87 face → 51 size → 55 qty) →
//   Right Jamb group (88 face → 52 size → 56 qty)
// Option type 80 (generic Add-On Face) is superseded by per-add-on face types 85-88.
const ADD_ONS_OPTION_TYPE_ORDER = [83, 82, 85, 50, 54, 86, 53, 57, 87, 51, 55, 88, 52, 56];
const ADD_ONS_DEPRECATED_TYPES = new Set([80, 44, 45, 46]);

// Maps each per-add-on face type (85-88) and detail types (size/qty) to the keyword
// that identifies their parent option in the Add Ons multi-select (type 82).
const ADD_ONS_TYPE_PARENT_KEYWORD: Record<number, string> = {
  85: 'head', 50: 'head', 54: 'head',
  86: 'cill', 53: 'cill', 57: 'cill',
  87: 'left',  51: 'left',  55: 'left',
  88: 'right', 52: 'right', 56: 'right',
};

// For size/qty types, the face type that gates them (None face = skip size/qty).
const ADD_ONS_SIZE_QTY_FACE_TYPE: Record<number, number> = {
  50: 85, 54: 85,   // Head size/qty gated by Head Face (85)
  53: 86, 57: 86,   // Cill size/qty gated by Cill Face (86)
  51: 87, 55: 87,   // Left Jamb size/qty gated by Left Jamb Face (87)
  52: 88, 56: 88,   // Right Jamb size/qty gated by Right Jamb Face (88)
};

/**
 * Generate JSON flow from categories and options
 * @param existingFlow - Optional existing flow to preserve step numbers
 */
export const generateJsonFlow = (
  categories: ICategory[],
  allOptions: IConfigOption[],
  products: IProduct[] = [],
  existingFlow?: IJsonFlow,
): IJsonFlow => {
  const questions: IFlowQuestion[] = [];
  let questionCounter = 1;

  // Create a map of existing questions by their unique key → step number
  const existingStepMap = new Map<string, number>();
  let maxExistingStep = 0;

  if (existingFlow?.questionFlow) {
    existingFlow.questionFlow.forEach((q) => {
      // Use responsePath as the unique key (it's stable and unique)
      // For questions without responsePath, fall back to questionId + category
      const key =
        q.responsePath || `${q.questionId}_${q.category}_${q.optionType}`;
      existingStepMap.set(key, q.step);
      maxExistingStep = Math.max(maxExistingStep, q.step);
    });
  }

  let nextAvailableStep = maxExistingStep + 1;

  // Helper function to get step number (preserved or new)
  const getStepForQuestion = (questionKey: string): number => {
    if (existingStepMap.has(questionKey)) {
      return existingStepMap.get(questionKey)!;
    }
    return nextAvailableStep++;
  };

  // Filter to only include active products (statecode = 0)
  const activeProducts = products.filter((p) => p.statecode === 0);

  // Get max number of frames from active products
  const maxFrames =
    activeProducts.length > 0
      ? Math.max(...activeProducts.map((p) => p.quantityonhand || 1))
      : 5; // Default to 5 if no products

  // Generate initial product selection questions (First Two Questions)
  const productQuestions = generateFirstTwoQuestions(
    activeProducts,
    categories,
    questionCounter,
    getStepForQuestion,
    allOptions,
  );
  questions.push(...productQuestions);
  questionCounter += productQuestions.length;

  // Sort categories by order defined in CATEGORY_TYPE_OPTIONS
  const sortedCategories = [...categories].sort((a, b) => {
    const orderA =
      CATEGORY_TYPE_OPTIONS.findIndex(
        (opt) => opt.value === a.dogma_categorytype,
      ) ?? 999;
    const orderB =
      CATEGORY_TYPE_OPTIONS.findIndex(
        (opt) => opt.value === b.dogma_categorytype,
      ) ?? 999;
    return orderA - orderB;
  });

  sortedCategories.forEach((category) => {
    // Get options for this category
    const categoryOptions = allOptions.filter(
      (opt) => opt._dogma_category_value === category.dogma_categoryid,
    );

    if (categoryOptions.length === 0) {
      return;
    }

    // Group options by optionType first
    const groupedByType = categoryOptions.reduce(
      (acc, option) => {
        const typeKey = option.dogma_optiontype?.toString() || "default";
        if (!acc[typeKey]) {
          acc[typeKey] = [];
        }
        acc[typeKey].push(option);
        return acc;
      },
      {} as Record<string, IConfigOption[]>,
    );

    // For Add Ons category: sort option types into the required flow order.
    // Type 80 (generic Add-On Face) is not shown as a standalone question —
    // instead its options are cloned into 4 per-add-on face questions (85-88),
    // one for each add-on selected in the Add Ons (82) multi-select.
    const isAddOnsCategory = category.dogma_categorytype === 10;
    let typeEntries = Object.entries(groupedByType);
    if (isAddOnsCategory) {
      const addOnsBaseOptions = groupedByType['82'] ?? [];
      const faceTemplateOptions = groupedByType['80'] ?? [];

      // Synthesise per-add-on face entries from the type-80 template options.
      // Each entry uses the same face choices (None/Front/Back/Front & Back) but is
      // scoped to one add-on via a displayCondition dependency.
      const ADD_ON_FACE_GROUPS: Array<{ faceType: number; keyword: string }> = [
        { faceType: 85, keyword: 'head' },
        { faceType: 86, keyword: 'cill' },
        { faceType: 87, keyword: 'left' },
        { faceType: 88, keyword: 'right' },
      ];

      if (faceTemplateOptions.length > 0 && addOnsBaseOptions.length > 0) {
        for (const { faceType, keyword } of ADD_ON_FACE_GROUPS) {
          if (groupedByType[String(faceType)]) continue; // explicit options already exist
          const parentOption = addOnsBaseOptions.find((o) =>
            o.dogma_name?.toLowerCase().includes(keyword),
          );
          if (!parentOption) continue;
          const dep = JSON.stringify({
            enabled: true,
            dependsOn: [{
              categoryId: category.dogma_categoryid,
              optionIds: [parentOption.dogma_configurationoptionsid],
            }],
          });
          groupedByType[String(faceType)] = faceTemplateOptions.map((o) => ({
            ...o,
            dogma_optiontype: faceType,
            // Clear the inherited FormattedValue so the generator resolves the
            // correct label ("Head Face", "Cill Face", etc.) from ALL_OPTION_TYPES.
            'dogma_optiontype@OData.Community.Display.V1.FormattedValue': undefined,
            dogma_displaycondition: dep,
          }));
        }
      }

      // Remove the generic type-80 entry and deprecated individual-addon yes/no types,
      // then sort all remaining entries into the required question order.
      typeEntries = Object.entries(groupedByType)
        .filter(([tk]) => !ADD_ONS_DEPRECATED_TYPES.has(parseInt(tk)))
        .sort(([a], [b]) => {
          const ai = ADD_ONS_OPTION_TYPE_ORDER.indexOf(parseInt(a));
          const bi = ADD_ONS_OPTION_TYPE_ORDER.indexOf(parseInt(b));
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });

      // Non-None face option IDs (Back / Front / Front & Back) from the type-80 template.
      // These are used to gate size/qty questions: only show when a real face is chosen.
      const nonNoneFaceOptionIds = faceTemplateOptions
        .filter((o) => o.dogma_name?.toLowerCase() !== 'none')
        .map((o) => o.dogma_configurationoptionsid);

      // For every option type that has an add-on parent keyword:
      //  • Inject the add-on parent dependency (which add-on was selected in type-82)
      //  • For size/qty types, also inject the face non-None dependency so the question
      //    is skipped when the user chose "None" for that add-on's face question.
      typeEntries = typeEntries.map(([tk, opts]) => {
        const optTypeNum = parseInt(tk);
        const keyword = ADD_ONS_TYPE_PARENT_KEYWORD[optTypeNum];
        if (!keyword || addOnsBaseOptions.length === 0) return [tk, opts];
        const parentOption = addOnsBaseOptions.find((o) =>
          o.dogma_name?.toLowerCase().includes(keyword),
        );
        if (!parentOption) return [tk, opts];

        const addOnDep = {
          categoryId: category.dogma_categoryid,
          optionIds: [parentOption.dogma_configurationoptionsid],
        };

        // Size/qty types additionally require the face question to be non-None.
        const isSizeOrQty = optTypeNum in ADD_ONS_SIZE_QTY_FACE_TYPE;
        const faceDep = isSizeOrQty && nonNoneFaceOptionIds.length > 0
          ? { categoryId: category.dogma_categoryid, optionIds: nonNoneFaceOptionIds }
          : null;

        const patched = opts.map((o) => {
          const existing = parseDisplayCondition(o.dogma_displaycondition ?? undefined);
          const existingDeps: any[] = existing?.dependsOn ?? [];

          // Build merged dependsOn: preserve existing deps, add add-on dep if missing,
          // add face non-None dep if applicable and not already present.
          const hasAddOnDep = existingDeps.some((d: any) =>
            d.optionIds?.includes(parentOption.dogma_configurationoptionsid),
          );
          const hasFaceDep = faceDep
            ? existingDeps.some((d: any) =>
                faceDep.optionIds.some((id: string) => d.optionIds?.includes(id)),
              )
            : true;

          if (hasAddOnDep && hasFaceDep) return o;

          const newDeps = [
            ...existingDeps,
            ...(hasAddOnDep ? [] : [addOnDep]),
            ...(faceDep && !hasFaceDep ? [faceDep] : []),
          ];
          return {
            ...o,
            dogma_displaycondition: JSON.stringify({ enabled: true, dependsOn: newDeps }),
          };
        });
        return [tk, patched] as [string, IConfigOption[]];
      });
    }

    // Create a question for each option type group, further grouped by inputType and display conditions
    typeEntries.forEach(([typeKey, options]) => {
      const optionTypeValue =
        typeKey === "default" ? undefined : parseInt(typeKey);
      // Get option type name from the formatted value in the option data
      const optionTypeName =
        options[0]?.[
          "dogma_optiontype@OData.Community.Display.V1.FormattedValue"
        ] ||
        (optionTypeValue
          ? ALL_OPTION_TYPES.find((t) => t.value === optionTypeValue)?.label ||
            "Other"
          : "General");

      // Further group by inputType within each optionType
      // Always use numeric key (default 5 = single select) so that options with
      // dogma_inputtype undefined/null/0 are grouped together with explicit 5,
      // not placed in a separate "single_select" bucket.
      const groupedByInputType = options.reduce(
        (acc, option) => {
          const inputTypeKey = option.dogma_inputtype || 5;
          if (!acc[inputTypeKey]) {
            acc[inputTypeKey] = [];
          }
          acc[inputTypeKey].push(option);
          return acc;
        },
        {} as Record<string, IConfigOption[]>,
      );

      // Create questions for each inputType group
      Object.entries(groupedByInputType).forEach(
        ([inputTypeKey, inputTypeOptions]) => {
          // Split options into:
          // 1. Base options: no displayCondition or no dependencies
          // 2. Conditional options: have displayCondition with dependencies
          const baseOptions: IConfigOption[] = [];
          const conditionalOptions: IConfigOption[] = [];

          inputTypeOptions.forEach((opt) => {
            const displayCondition = parseDisplayCondition(
              opt.dogma_displaycondition ?? undefined,
            );
            const hasDependencies =
              displayCondition?.enabled &&
              displayCondition?.dependsOn &&
              displayCondition.dependsOn.length > 0;

            if (hasDependencies) {
              conditionalOptions.push(opt);
            } else {
              baseOptions.push(opt);
            }
          });

          // Group conditional options by dependencies
          const groupedByDependencies = groupByDependencies(conditionalOptions);
          const multiOptionGroups: IConfigOption[][] = [];
          const singleConditionalOptions: IConfigOption[] = [];

          groupedByDependencies.forEach((dependencyGroup) => {
            if (dependencyGroup.length === 1 && dependencyGroup[0]) {
              // Single option with dependency → add to base question
              singleConditionalOptions.push(dependencyGroup[0]);
            } else if (dependencyGroup.length > 1) {
              // Multiple options with same dependency → create separate question
              multiOptionGroups.push(dependencyGroup);
            }
          });

          // Combine all options into one question — conditional options keep their
          // individual displayCondition so the runtime shows/hides them correctly.
          const allBaseOptions = [
            ...baseOptions,
            ...singleConditionalOptions,
            ...multiOptionGroups.flat(),
          ];

          // Create base question if there are any base options or single conditional options
          if (allBaseOptions.length > 0) {
            const sortedOptions = [...allBaseOptions];

            // Check if any option has repeatPerFrame enabled, or if the
            // category is Trickle Vents (type 8) — trickle vent placement
            // and sizing must always be asked per-frame.
            // For Glazing (type 9), specific option types are also per-frame
            // when the glass glazing type is selected (dependency carried via
            // each option's dogma_displaycondition).
            const hasRepeatPerFrame =
              sortedOptions.some((opt) => opt.dogma_repeatperwindowframe) ||
              category.dogma_categorytype === 8 ||
              (category.dogma_categorytype === 9 &&
                optionTypeValue !== undefined &&
                GLAZING_PER_FRAME_OPTION_TYPES.has(optionTypeValue));

            // Special handling: split Glazing Pattern (31) into Top/Bottom sash questions
            // while reusing the same option list + display conditions.
            if (optionTypeValue === 31) {
              const split = [
                {
                  optionTypeValue: 92,
                  optionTypeName: "Top Sash Glazing Pattern",
                },
                {
                  optionTypeValue: 93,
                  optionTypeName: "Bottom Sash Glazing Pattern",
                },
              ];

              if (hasRepeatPerFrame) {
                for (const s of split) {
                  const result = generatePerWindowQuestions(
                    sortedOptions,
                    category,
                    s.optionTypeValue,
                    s.optionTypeName,
                    typeKey,
                    inputTypeKey,
                    questionCounter,
                    getStepForQuestion,
                    maxFrames,
                    activeProducts,
                    false, // not a conditional group
                    allOptions, // pass all options to find config mode options dynamically
                  );
                  questions.push(...result.questions);
                  questionCounter = result.newQuestionCounter;
                }
              } else {
                for (const s of split) {
                  const question = generateNormalQuestion(
                    sortedOptions,
                    category,
                    s.optionTypeValue,
                    s.optionTypeName,
                    typeKey,
                    inputTypeKey,
                    questionCounter,
                    getStepForQuestion,
                  );
                  questions.push(question);
                  questionCounter++;
                }
              }

              return; // done for this inputType group
            }

            if (hasRepeatPerFrame) {
              // Use generatePerWindowQuestions for repeating questions
              const result = generatePerWindowQuestions(
                sortedOptions,
                category,
                optionTypeValue,
                optionTypeName,
                typeKey,
                inputTypeKey,
                questionCounter,
                getStepForQuestion,
                maxFrames,
                activeProducts,
                false, // not a conditional group
                allOptions, // pass all options to find config mode options dynamically
              );
              questions.push(...result.questions);
              questionCounter = result.newQuestionCounter;
            } else {
              // Use generateNormalQuestion for non-repeating questions
              const question = generateNormalQuestion(
                sortedOptions,
                category,
                optionTypeValue,
                optionTypeName,
                typeKey,
                inputTypeKey,
                questionCounter,
                getStepForQuestion,
              );

              // Special handling for Configuration Mode questions (optionType 49)
              // Only show for multi-frame products (Bays=3 and Coupled=4)
              if (optionTypeValue === 49) {
                const multiFrameProducts = activeProducts.filter(
                  (p) => p.producttypecode === 3 || p.producttypecode === 4,
                );
                const multiFrameProductIds = multiFrameProducts.map(
                  (p) => p.productid,
                );

                // Find the decorative bar type options for this category (optionType 63)
                const decorativeBarTypeOptions = allOptions.filter(
                  (opt) =>
                    opt.dogma_optiontype === 63 &&
                    opt._dogma_category_value === category.dogma_categoryid,
                );

                // Get all decorative bar type option IDs EXCEPT "None"
                const decorativeBarOptionIds = decorativeBarTypeOptions
                  .filter(
                    (opt) =>
                      !opt.dogma_name?.toLowerCase().includes("none") &&
                      opt.dogma_value?.toLowerCase() !== "none",
                  )
                  .map((opt) => opt.dogma_configurationoptionsid);

                // Build dependsOn array
                const dependsOnArray =
                  question.displayCondition?.dependsOn || [];

                // Add dependency on decorative bar type if we found options
                if (
                  decorativeBarOptionIds.length > 0 &&
                  category.dogma_categoryid
                ) {
                  dependsOnArray.push({
                    categoryId: category.dogma_categoryid,
                    optionIds: decorativeBarOptionIds,
                  });
                }

                question.displayCondition = {
                  enabled: true,
                  dependsOn: dependsOnArray,
                  validForProducts: multiFrameProductIds,
                };

                // Also update each option's validProducts
                question.options = question.options.map((opt) => ({
                  ...opt,
                  displayCondition: {
                    ...(opt.displayCondition || {}),
                    enabled: true,
                    validForProducts: multiFrameProductIds,
                  },
                }));
              }

              questions.push(question);
              questionCounter++;
            }
          }

        },
      );

    });
  });

  // Inject external colour option IDs into the internal colour question's existing
  // dependsOn entry (same Profile Colours category) so that when the user changes
  // external colour, internal colour is re-asked.
  const externalColourOptions = allOptions.filter(opt => opt.dogma_optiontype === 34);
  if (externalColourOptions.length > 0) {
    const externalColourCategoryId = externalColourOptions[0]?._dogma_category_value ?? '';
    const externalColourOptionIds = externalColourOptions.map(opt => opt.dogma_configurationoptionsid);

    questions.forEach(q => {
      if (q.optionType !== 35) return; // Internal Colour only
      if (!externalColourCategoryId) return;

      const existingDeps: any[] = q.displayCondition?.dependsOn ?? [];
      // Find the existing entry for this category and merge IDs into it
      const existingEntry = existingDeps.find(d => d.categoryId === externalColourCategoryId);
      if (existingEntry) {
        const merged = new Set<string>([...(existingEntry.optionIds ?? []), ...externalColourOptionIds]);
        existingEntry.optionIds = Array.from(merged);
      } else {
        existingDeps.push({ categoryId: externalColourCategoryId, optionIds: externalColourOptionIds });
      }

      q.displayCondition = {
        ...(q.displayCondition ?? { enabled: true }),
        dependsOn: existingDeps,
      };
    });
  }

  return {
    productLine: "EcoSlide Vertical Sliding Sash Windows",
    chatbotConfig: {
      greeting: "Hello! I'm here to help you configure your perfect window.",
      language: "en-GB",
      currency: "GBP",
      units: "metric",
      defaultRegion: "UK",
    },
    questionFlow: questions,
    metadata: {
      generatedAt: new Date().toISOString(),
      totalCategories: sortedCategories.length,
      totalQuestions: questions.length,
    },
  };
};

// Removed: mapInputTypeToQuestionType - now in questions.ts

/**
 * Group options by their dependencies (dependsOn)
 * Options with the same dependencies will be grouped together into one question
 */
const groupByDependencies = (options: IConfigOption[]): IConfigOption[][] => {
  const groups = new Map<string, IConfigOption[]>();

  options.forEach((option) => {
    // Create a unique key based on dependencies
    const dependencyKey = getDependencyKey(
      parseDisplayCondition(option.dogma_displaycondition ?? undefined),
    );

    if (!groups.has(dependencyKey)) {
      groups.set(dependencyKey, []);
    }
    groups.get(dependencyKey)!.push(option);
  });

  return Array.from(groups.values());
};

/**
 * Generate a unique key for dependencies
 * Options with the same dependsOn conditions will have the same key
 */
const getDependencyKey = (condition?: any): string => {
  if (
    !condition ||
    !condition.enabled ||
    !condition.dependsOn ||
    condition.dependsOn.length === 0
  ) {
    return "no-dependencies";
  }

  // Create unique key based on dependsOn array
  const dependsOnKey = condition.dependsOn
    .map(
      (dep: any) =>
        `${dep.categoryId}:${(dep.optionIds || []).sort().join(",")}`,
    )
    .sort()
    .join("|");

  return dependsOnKey;
};

// Removed: extractCommonDisplayCondition - now in questions.ts
// Removed: generateQuestionText - now in questions.ts
// Removed: generateProductSelectionQuestions - replaced with generateFirstTwoQuestions in questions.ts
